import React from 'react';
import { IReactionDisposer, reaction } from 'mobx';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    DrawerService,
    InjectorService,
    EventEmitter,
    BuiltinUiState,
    StateManager,
    ToolbarService,
    ControllerService,
} from '@tencent/workbench';
import { DrawerName, FormPanelName, FormSectionCollapsed, INPUT_PROPS_CALLBACK_KEY, WbEventNames } from '@framework/workbench/drawer/constants';
import FormContent from '@framework/workbench/drawer/components/form-content';
import { FormService } from '@tencent/wb-ext-form';
import type { Subscription } from 'rxjs';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import { SizeFeature } from '@framework/workbench/drawer/ui/layout/size/size-feature';
import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import debounce from 'lodash/debounce';
import Snackbar from '@tencent/dui/lib/components/Snackbar';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import i18n from '@i18next/i18n';
import { IMenuManager } from '@src/slide/application/service/menu-manager/menu-manager.interface';
import FormSchemaLogic from './form-schema-logic';
import { FormSwitchManager, IMAGE_LABEL, NO_ACTION } from './form-switch-manager';
import { IRightSideBarManager } from '@src/slide/application/service/right-side-bar-manager/right-side-bar-manager.interface';
import { IMasterLayoutEditService } from '@src/slide/application/service/master-layout-edit-service/master-layout-edit-service.interface';
import { IUserPriviledgeService } from '@src/slide/application/service/user-priviledge-service/user-priviledge-service.interface';
import { ISelectionService, SelectedModelType } from '@src/slide/application/service/selection/selection-service.interface';
import { SlideFooterFeature } from '@framework/workbench/drawer/ui/slide-footer/slide-footer-feature';
import { PictureColorFeature } from '@framework/workbench/drawer/ui/picture/picture-color/picture-color-feature';
import { FillType } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { getAllImage } from '@src/slide/application/service/menu-manager/menu-values/element-menu-values-getter';
import { IChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-model.interface';
import type { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';
import type { ICopyPasteImageService } from '@src/slide/application/service/copy-paste/copy-paste.interface';
import { IDrawerStateManager } from '@src/slide/application/service/drawer-state-manager/core/drawer-state-manager.interface';
import {
    IPropertyEditorInitProps,
    IPropertyEditorManager,
    IPropertyEditorProps,
} from '@src/slide/application/service/property-editor-manager/property-editor-manager.interface';
import { IRightSideBarService } from '../right-side-bar-service/core/interface';
import {
    ModelMenuRefreshEventsKeys,
} from '@src/slide/domain/ui-model/element-modeles/Interface/model-menu-refresh.interface';
import { IDrawerInputManager } from '../drawer-input-manager/core/drawer-state-manager.interface';
import { getPcInjectedService } from '@src/framework/workbench/util/injector-service';
import { ChartService } from '@src/framework/workbench/common/service/chart-service';
import { ELEMENT_TAB_MAP } from '@src/framework/workbench/schema/constant';
import DrawerTopTab from '@framework/workbench/drawer/components/drawer-top-tab';
import { FillTypeFeature } from '@framework/workbench/drawer/ui/fill/fill-type/fill-type-feature';

const DRAWER_INPUT_CLASS_NAME = 'number-input_number-input__';

class PropertyEditorManager implements IPropertyEditorManager {
    private showFormat: boolean; // 打开侧边栏-格式
    private showAnimation: boolean; // 打开侧边栏-动画
    private drawerService: DrawerService;
    private formService: FormService;
    private fillFeature: FillTypeFeature;
    private slideFooterFeature: SlideFooterFeature;
    private sizeFeature: SizeFeature;
    private pictureColorFeature: PictureColorFeature;
    private selectedModel: any;
    private rightReaction: any;
    private showReaction: any;
    private selectedModelReaction: IReactionDisposer | null;
    private chartElementsReaction: IReactionDisposer | null;
    private animationReaction;
    private slideChangeReaction: IReactionDisposer | null;
    private formItemlistener: Subscription | undefined;
    private formNumberInputListener: Subscription | undefined;
    private formNumberInputFocusListener: Subscription | undefined;
    private rightDrawerOpenListener: Subscription | undefined;
    private eventEmitter: EventEmitter;
    private snackbarOp;
    private fillClipBoardReaction: IReactionDisposer | undefined;
    private menuManager: IMenuManager;
    private rightSideBarManager: IRightSideBarManager | undefined;
    private formSwitchManager: FormSwitchManager;
    private selectionService: ISelectionService;
    private masterLayoutEditService: IMasterLayoutEditService | undefined;
    private copyPasteImageService?: IDynamicService<ICopyPasteImageService>;
    private drawerStateManager?: IDrawerStateManager;
    private drawerInputManager?: IDrawerInputManager;
    private userPriviledgeService: IUserPriviledgeService;
    private rightSideBarService?: IRightSideBarService;
    private chartService: ChartService;
    private tabElement: Element | null;
    private controllerService: ControllerService;

    // 监听group-model的selectedElementItemValues是否有变化
    private handleGroupMultiChange = debounce(() => {
        // 当仅UI改变时，无需触发更新
        if (this.drawerInputManager?.isOnlyChangeUI) return;
        this.setSchema(true);
        const type = this.selectionService.getSelectedModel()?.type;
        if (!type || ![UIModelType.GROUP, UIModelType.TABLE, UIModelType.MULTIPLY].includes(type)) return;
        this.drawerInputManager?.executeInitPropsCallback();
    });

    public setDelegate = ({ menuManager, rightSideBarManager, selectionService,
        masterLayoutEditService,
        userPriviledgeService, copyPasteImageService,
        rightSideBarService }: IPropertyEditorProps) => {
        this.menuManager = menuManager;
        this.rightSideBarManager = rightSideBarManager;
        this.selectionService = selectionService;
        this.masterLayoutEditService = masterLayoutEditService;
        this.copyPasteImageService = copyPasteImageService;
        this.userPriviledgeService = userPriviledgeService;
        this.rightSideBarService = rightSideBarService;
    };

    public init = ({ drawerStateManager, drawerInputManager }: IPropertyEditorInitProps) => {
        this.drawerStateManager = drawerStateManager;
        this.drawerInputManager = drawerInputManager;
        this.drawerService = InjectorService.get(DrawerService);
        this.formService = InjectorService.get(FormService);
        this.fillFeature = InjectorService.get(FillTypeFeature);
        this.slideFooterFeature = InjectorService.get(SlideFooterFeature);
        this.eventEmitter = InjectorService.get(EventEmitter);
        this.sizeFeature = InjectorService.get(SizeFeature);
        this.controllerService = InjectorService.get(ControllerService);
        // 记录选中元素的初始大小 - 用于侧边栏缩放高度/宽度设置
        this.sizeFeature.modelOriginalSizeMap = new Map();
        this.pictureColorFeature = InjectorService.get(PictureColorFeature);
        this.formSwitchManager = new FormSwitchManager(
            this.rightSideBarManager,
            this.selectionService,
            this.rightSideBarService,
        );
        this.chartService = getPcInjectedService(ChartService) as ChartService;
        this.registListener();
        FormSchemaLogic.init({
            menuManager: this.menuManager,
            userPriviledgeService: this.userPriviledgeService,
            masterLayoutEditService: this.masterLayoutEditService,
            rightSideBarManager: this.rightSideBarManager,
        });

        import(/* webpackChunkName: "drawing-drawer-components" */ '@tencent/drawing-drawer-components')
            .then((module) => {
                const {
                    SolidFillController,
                    GradualFillController,
                    ImageFillController,
                    FillTypeController,
                    BackgroundFillController,
                    BorderController,
                    GlowController,
                    ReflectionController,
                    ShadowController,
                    SoftEdgeController,
                } = module;
                this.controllerService.buildController(SolidFillController);
                this.controllerService.buildController(GradualFillController);
                this.controllerService.buildController(ImageFillController);
                this.controllerService.buildController(FillTypeController);
                this.controllerService.buildController(BackgroundFillController);
                this.controllerService.buildController(BackgroundFillController);
                this.controllerService.buildController(BorderController);
                this.controllerService.buildController(GlowController);
                this.controllerService.buildController(ReflectionController);
                this.controllerService.buildController(ShadowController);
                this.controllerService.buildController(SoftEdgeController);
            })
            .catch((error) => {
                window.log.error(`[loadChunk-error]${error}`);
            });
    };

    public registListener() {
        this.rightReaction = reaction(
            () => this.rightSideBarManager?.isPropertyEditorShown,
            (show) => {
                this.updateObserverForSelectedModel(show);
                if (show) {
                    // 打开面板，需要更新schema
                    FormSchemaLogic.setFormSchema(this.selectionService.getSelectedModel());
                    this.drawerStateManager?.openTab(DrawerName.SLIDE_PROPERTY_DRAWER);
                } else {
                    this.drawerStateManager?.closeRightDrawer(DrawerName.SLIDE_PROPERTY_DRAWER);
                }
                this.handleShowPropertyDrawer();
            },
        );

        // 从其他位置打开属性面版，比如'工具栏-效果-更多设置'。
        this.showReaction = reaction(
            () => this.rightSideBarManager?.propertyEditorShowAction,
            (hasAction) => {
                if (hasAction !== 0) {
                    if (this.isOtherDrawerOpen()) {
                        this.formSwitchManager.toggleWithAction(false, hasAction);
                        // 当打开格式侧边栏的情况下+打开其他侧边，此时重新打开格式侧边栏，需手动触发打开格式侧边栏
                        this.showRightDrawer(false);
                    } else {
                        this.formSwitchManager.toggleWithAction(this.showFormat, hasAction);
                    }
                }
            },
        );

        // 监听选中元素
        this.selectedModelReaction = reaction(
            () => ({
                selectedModel: this.selectionService.getSelectedModel(),
            }),
            ({ selectedModel }) => {
                // 切换tab
                this.changeToolbarTab(selectedModel?.type);

                this.initDrawerInputStateAfterModelChanged(selectedModel);
                this.pictureColorFeature.clearRecolorCache();
                this.initModelOriginalSize(selectedModel);
                if (this.rightSideBarManager?.isPropertyEditorShown
                    || this?.masterLayoutEditService?.isMasterEditType) {
                    // 选中了图表，并选中图表内的元素， 会在图表元素的监听中打开侧边栏
                    if (selectedModel?.type === UIModelType.CHART
                        && (selectedModel as IChartModel).selectedElement?.id) {
                        return;
                    }
                    // 初始化侧边栏数据
                    this.initDrawerData();
                    // 切换选中元素，需要更新schema配置项
                    FormSchemaLogic.setFormSchema(selectedModel);
                }
            },
        );

        // 监听图表选中元素
        this.chartElementsReaction = reaction(
            () => (this.selectionService.getSelectedModel() as IChartModel).selectedElement,
            () => {
                if (!this.rightSideBarManager?.isPropertyEditorShown) return;
                const model = this.selectionService.getSelectedModel() as IChartModel;
                // 图表内选中不同组件， 切换对应侧边栏
                if (model?.type === UIModelType.CHART) {
                    FormSchemaLogic.setFormSchema(model);
                    if (model.selectedElement?.id) {
                        // 初始化侧边栏数据
                        this.initDrawerData();
                    }
                }
            },
        );

        // 监听母版编辑的变化
        this.selectedModelReaction = reaction(
            () => ({ isMasterEditType: this?.masterLayoutEditService?.isMasterEditType }),
            () => {
                FormSchemaLogic.setFormSchema(this.selectionService.getSelectedModel());
                const manager = this.drawerStateManager;
                manager?.closeRightDrawerDirectly([DrawerName.SLIDE_PROPERTY_DRAWER, DrawerName.MACHINE_AUDIT]);
                manager?.closeAllTab([DrawerName.SLIDE_PROPERTY_DRAWER, DrawerName.MACHINE_AUDIT]);
            },
        );
    }

    public removeListener() {
        this.rightReaction?.();
        this.animationReaction?.();
        this.showReaction?.();
        this.selectedModelReaction?.();
        this.chartElementsReaction?.();
    }

    public changeToolbarTab = (elementType?: UIModelType) => {
        // BuiltinUiState.RibbonActiveTab记录了上次手动切换的tab的信息
        const state = InjectorService.get(StateManager);
        const tab = state.get(BuiltinUiState.RibbonActiveTab).value as string;
        const elementTabs = ELEMENT_TAB_MAP[elementType || ''];
        if (elementTabs?.length && elementTabs.includes(tab)) {
            const toolbarService = InjectorService.get(ToolbarService);
            toolbarService?.changeRibbonTab(tab);
        }
    };


    public close = () => {
        this.handleDrawerClose();
        this.drawerStateManager?.closeTab(DrawerName.SLIDE_PROPERTY_DRAWER);
    };

    private handleShowPropertyDrawer = () => {
        const show = this.rightSideBarManager?.isPropertyEditorShown;
        if (show) {
            this.setSchema();
            this.formSwitchManager.changeFormToFormat(this.showFormat, FormPanelName.SHAPE);
            this.formSwitchManager.handleLabelCollapse(); // 打开默认设置项
            this.drawerInputManager?.executeInitPropsCallback();   // 初始化数据
            if (this.showFormat) {
                this.closeRightSideBar();
                return;
            }
            this.registPropertyDrawerListener();
            this.closeRightSideBarWhenDrawerOpen();
            this.reportDrawerOpen();
            this.changeShowState();
            this.showRightDrawer();
        } else {
            this.handleCloseDrawer();
            this.removePropertyDrawerListener();
        }
    };

    private handleCloseDrawer = () => {
        this.changeShowDetailState(false);
        const isPropertyEditorShown = this.rightSideBarManager?.isPropertyEditorShown;
        if (isPropertyEditorShown) return;
        window.dispatchEvent(new Event('resize')); // 主动触发window resize
    };

    /**
     * 侧边栏从关闭到打开时关闭sideBar
     */
    private closeRightSideBarWhenDrawerOpen = () =>  {
        this.closeRightSideBar();
        this.rightSideBarManager?.setIsSelectWindowShown(false);
    };

    /**
     * 打开侧边栏时上报数据
     */
    private reportDrawerOpen = () => {
        const model = this.selectionService.getSelectedModel();
        this.report(model);
    };

    private changeShowState() {
        this.changeShowDetailState(true);
        window.dispatchEvent(new Event('resize')); // 主动触发window resize
        this.setBgWithActionWithEditorShown();
    }

    private showRightDrawer = (needReport = true) => {
        this.drawerService.showRightDrawer({
            name: DrawerName.SLIDE_PROPERTY_DRAWER,
            content: <DrawerTopTab><FormContent close={this.close} /></DrawerTopTab>,
            onClose: this.handleDrawerClose,
        });
        this.drawerStateManager?.handleDrawerHolderVisiable(true);
        if (needReport) {
            // 上报数据
            this.fillFeature.tdwDrawerReport(this.fillFeature.tdwDrawerConstMap.drawerOpen);
        }
    };

    private handleDrawerClose = () => {
        this.drawerStateManager?.handleDrawerHolderVisiable(false);
        // 当格式侧边栏打开时，再打开其他侧边栏，关闭其他侧边栏后应该重新打开格式侧边栏
        if (this.isOtherDrawerOpen()) {
            this.rightDrawerOpenListener = this.eventEmitter.listen(WbEventNames.RIGHT_DRAWER_CLOSED, () => {
                this.showRightDrawer(false);
                this.rightDrawerOpenListener?.unsubscribe();
            });
            return;
        }
        this.rightSideBarManager?.setIsPropertyEditorShown(false);
    };

    private addObserverForSelectedModel(model) {
        if (model?.changeSetEngine) {
            model.changeSetEngine.addChangesetObserver(this.handleShowPropertyDrawer);
        }
    }

    private removeObserverForSelectedModel(model) {
        if (model?.changeSetEngine) {
            model.changeSetEngine.removeChangesetObserver(this.handleShowPropertyDrawer);
        }
    }

    private report(model) {
        let action;
        switch (model.type) {
            case UIModelType.EMPTY:
                action = TDWActionType.BACKGROUD_UPV;
                break;
            case UIModelType.SHAPE:
                action = TDWActionType.SHAPE_UPV;
                break;
            case UIModelType.IMAGE:
                action = TDWActionType.PIC_UPV;
                break;
        }
        if (action) {
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.PC_SIDE_BAR,
                action,
            });
        }
    }


    // 判断是否需要直接设上背景
    private setBgWithActionWithEditorShown = () => {
        if (this.rightSideBarManager?.isPropertyEditorShown) {
            this.setBgWithAction();
        }
    };

    private setBgWithAction = () => {
        const action = this.rightSideBarManager?.propertyEditorShowAction;
        this.fillFeature.setBgWithAction(action);
        this.rightSideBarManager?.setPropertyEditorShowAction(NO_ACTION);
    };

    private closeRightSideBar = () => {
        this.rightSideBarService?.hide();
    };

    private registPropertyDrawerListener = () => {
        this.getImageBgFromClipBoardReaction().then((dispose) => {
            this.fillClipBoardReaction = dispose;
        });
        this.formItemlistener = this.eventEmitter.listen(
            FormSectionCollapsed,
            this.formSwitchManager.handleItemCollapsed,
        );
        this.slideChangeReaction = reaction(
            () => this.selectionService.currentSlide,
            () => {
                this.drawerInputManager?.executeInitPropsCallback(INPUT_PROPS_CALLBACK_KEY.FILL);  // 切换幻灯片时仅需更新填充数据
                this.drawerInputManager?.executeInitPropsCallback(INPUT_PROPS_CALLBACK_KEY.COMMON_FILL);
                this.drawerInputManager?.executeInitPropsCallback(INPUT_PROPS_CALLBACK_KEY.GRADUAL_FILL);
                this.drawerInputManager?.executeInitPropsCallback(INPUT_PROPS_CALLBACK_KEY.IMAGE_FILL);
                this.drawerInputManager?.executeInitPropsCallback(INPUT_PROPS_CALLBACK_KEY.SOLID_FILL);
                this.slideFooterFeature.updateInputStateOnChangePage();
                this.formService.$forceUpdate();
            },
        );
        /**
         * case：侧边栏未打开的情况下，通过工具栏设置组合效果时，侧边栏打开。
         * 由于组合只有子元素会发changeSet，组合本身是不会发送的，这样会导致侧边栏的数据不是最新的数据，需要手动刷新下
         */
        this.menuManager.event.on(ModelMenuRefreshEventsKeys.REFRESH_MENU_VALUES_VALUES, this.handleGroupMultiChange);
        this.formNumberInputListener = this.eventEmitter.listen(
            WbEventNames.FORM_NUMBER_INPUT_BLUR,
            this.handleFormNumberInputBlur,
        );
        this.formNumberInputFocusListener = this.eventEmitter.listen(
            WbEventNames.FORM_NUMBER_INPUT_FOCUS,
            this.handleFormNumberInputFocus,
        );
    };

    private removePropertyDrawerListener = () => {
        this.formItemlistener?.unsubscribe();
        this.formItemlistener = undefined;
        this.fillClipBoardReaction?.();
        this.slideChangeReaction?.();
        this.menuManager.event.off(ModelMenuRefreshEventsKeys.REFRESH_MENU_VALUES_VALUES, this.handleGroupMultiChange);
        this.formNumberInputListener?.unsubscribe();
        this.formNumberInputListener = undefined;
        this.formNumberInputFocusListener?.unsubscribe();
        this.formNumberInputFocusListener = undefined;
        this.rightDrawerOpenListener?.unsubscribe();
        this.rightDrawerOpenListener = undefined;
    };

    /**
     * workbench输入框的blur事件先触发，然后触发输入框的onChange事件
     */
    private handleFormNumberInputBlur = () => {
        this.drawerInputManager?.setInputFocus(false);
        this.tabElement?.removeEventListener('keydown', this.handleInputEnter);
    };

    private handleFormNumberInputFocus = () => {
        const model = this.selectionService.getSelectedModel();
        if (model?.type === UIModelType.CHART) {
            // 修复图表选中元素后， 输入框输入后 切换元素失焦问题
            const selectedElement = this.chartService.getSelectedElementModel(model);
            this.drawerInputManager?.setInputFocus(true, selectedElement as unknown as SelectedModelType);
        } else {
            this.drawerInputManager?.setInputFocus(true);
        }
        // 输入框回车后，数据落盘并全选内容
        this.tabElement = document.getElementsByClassName('dui-tabs-content')?.[0];
        this.tabElement?.addEventListener('keydown', this.handleInputEnter);
    };

    private handleInputEnter = (event: KeyboardEvent) => {
        if ((event.keyCode === 13 || event.which === 13) && (event.target as HTMLElement).tagName === 'INPUT') {
            event.preventDefault();
            // 全选输入框的内容
            (event.target as HTMLInputElement).select();
            this.drawerInputManager?.forceUpdateInput();
        }
    };

    private updateObserverForSelectedModel = (needAdd = true) => {
        this.removeObserverForSelectedModel(this.selectedModel);
        if (needAdd) {
            this.selectedModel = this.selectionService.getSelectedModel();
            this.addObserverForSelectedModel(this.selectedModel);
        }
    };

    private changeShowDetailState = (isShown: boolean) => {
        this.showFormat = isShown;
    };

    /**
     * 侧边栏打开的情况下，初始化侧边栏数据
     */
    private initDrawerData() {
        this.updateObserverForSelectedModel();
        // 切换选中元素时初始化数据
        this.drawerInputManager?.executeInitPropsCallback();
        // 打开默认设置项
        this.formSwitchManager.handleLabelCollapse(true);
        // 初始化大小中的最佳放映比例
        this.sizeFeature.initBestPlayScale();
    }

    private initDrawerInputStateAfterModelChanged = (selectedModel) => {
        this.drawerInputManager?.updateCurrentModel(selectedModel);
        if (this.rightSideBarManager?.isPropertyEditorShown) {
            const { activeElement } = document;
            // 避免切换元素后，输入框未失焦的情况
            if (activeElement?.className.includes(DRAWER_INPUT_CLASS_NAME)) {
                (activeElement as HTMLElement).blur?.();
            }
        }
    };

    /**
     * 记录选中元素的初始大小
     */
    private initModelOriginalSize(selectedModel: IBaseModel | null | undefined): void {
        if (selectedModel?.type === UIModelType.MULTIPLY) {
            (selectedModel as IMultiplyModel).selectedModels.forEach((model: IElementBaseModel) => {
                this.updateModelOriginSizeMap(model);
            });
        } else if (selectedModel?.type === UIModelType.GROUP) {
            const groupModel = (selectedModel as IGroupModel);
            if (groupModel.isRootGroup) {
                this.updateModelOriginSizeMap(groupModel);
            }
            groupModel.children.forEach((model: IElementBaseModel) => {
                this.updateModelOriginSizeMap(model);
            });
        } else {
            if (typeof (selectedModel as IElementBaseModel).getFrame !== 'function') return;
            this.updateModelOriginSizeMap(selectedModel as IElementBaseModel);
        }
    }

    private updateModelOriginSizeMap = (selectedModel: IElementBaseModel): void => {
        const width = selectedModel.getFrame().getWidth();
        const height = selectedModel.getFrame().getHeight();
        this.sizeFeature.modelOriginalSizeMap.set(selectedModel.key, {
            width, height,
        });
    };

    // 图片填充剪贴板操作提示
    private getImageBgFromClipBoardReaction = async () => {
        const copyPasteImageService = await this.copyPasteImageService?.promise();
        const { ImageEditManager } = copyPasteImageService ?? {};
        const imageManager = ImageEditManager?.getInstance();
        return reaction(
            () => imageManager?.clipDataWaiting,
            (waiting) => {
                this.handleClipDataWaiting(waiting);
            },
        );
    };

    private async handleClipDataWaiting(waiting) {
        const copyPasteImageService = await this.copyPasteImageService?.promise();
        const { ImageEditManager } = copyPasteImageService ?? {};
        const imageManager = ImageEditManager?.getInstance();
        if (imageManager?.imageReplaceAction === 1) return; // 表示是替换图片
        if (waiting) {
            this.snackbarOp = Snackbar.show({
                message: browser.isMac ? i18n.t('请使用 Cmd+V 填充该图片') : i18n.t('请使用 Ctrl+V 填充该图片'),
                duration: 3000,
            });
        } else if (this.snackbarOp) {
            this.snackbarOp.close();
            this.snackbarOp = null;
        }
    }

    /**
     * 判断是否在打开格式侧边栏的情况下，其他如插件等使用wb drawer打开
     */
    private isOtherDrawerOpen = (): boolean | undefined => this.rightSideBarManager?.isPropertyEditorShown
        && !(this.drawerService.isRightOpening(DrawerName.SLIDE_PROPERTY_DRAWER)
            || this.drawerService.isRightOpening(DrawerName.ANIMATION_RIGHT_DRAWER)
            || this.drawerService.isRightOpening(DrawerName.COMMENT_DRAWER)
            || this.drawerService.isRightOpening(DrawerName.SELECT_WINDOW_RIGHT_DRAWER)
        )
        && this.drawerService.isRightOpening();

    private setSchema = (isMultiGroup?: boolean) => {
        const selectedModel = this.selectionService.getSelectedModel();
        let needSetSchema = false;
        const hasImageLabel = this.formSwitchManager.getLabelIndex(IMAGE_LABEL) !== -1;
        const type = selectedModel?.type;
        if (type === UIModelType.SHAPE) {
            const fillType = (selectedModel as IShapeModel).getBackgroundFill().getFillType();
            needSetSchema = fillType === FillType.ImageFill ? !hasImageLabel : hasImageLabel;
        } else if (isMultiGroup && [UIModelType.MULTIPLY, UIModelType.GROUP].includes(type!)) {
            const images = getAllImage(selectedModel as IMultiplyModel);
            needSetSchema = images.length === 1 ? !hasImageLabel : hasImageLabel;
        }
        if (needSetSchema) {
            FormSchemaLogic.setFormSchema(selectedModel);
        }
    };
}

export default PropertyEditorManager;
